/**
 * LogicCraft - Módulo de Blindagem, Segurança Client-Side e Integridade (core-guard.js)
 * Namespace: window.LogicCraftGuard
 *
 * Responsável por:
 * 1. Sanitização e Validação Estrutural de JSON (Anti-Malicious Import)
 * 2. Assinatura e Checksum de Integridade (SHA-256 / FNV-1a Fallback)
 * 3. Sanitização DOM e Prevenção de Injeções (XSS Safe Builders)
 * 4. Interceptador Global de Falhas e Modal de Recuperação (Crash Interceptor)
 * 5. Purga de Memória de Vídeo (Three.js VRAM Purge)
 * 6. Modelo Térmico Bimetálico Normalizado (NR-10 / Eletrotécnica)
 */

'use strict';

window.LogicCraftGuard = (function() {

    // Limites de Segurança do Simulador
    const MAX_PAYLOAD_BYTES = 2 * 1024 * 1024; // 2 MB
    const BENCH_COORD_MIN = -30;
    const BENCH_COORD_MAX = 30;
    const MAX_TAG_LENGTH = 10;

    /**
     * Algoritmo de Hashing FNV-1a (64-bit Hex)
     * Fallback síncrono ultra-rápido para contextos sem HTTPS/WebCrypto
     */
    function hashFNV1a(str) {
        let h1 = 0x811c9dc5, h2 = 0xcbf29ce4;
        for (let i = 0; i < str.length; i++) {
            const code = str.charCodeAt(i);
            h1 = Math.imul(h1 ^ code, 0x01000193);
            h2 = Math.imul(h2 ^ (code >>> 8), 0x01000193);
        }
        const hex1 = ('00000000' + (h1 >>> 0).toString(16)).slice(-8);
        const hex2 = ('00000000' + (h2 >>> 0).toString(16)).slice(-8);
        return hex1 + hex2;
    }

    /**
     * Implementação síncrona compacta de SHA-256 em JavaScript puro
     * Garante compatibilidade total e síncrona para chamadas ao localStorage
     */
    function sha256Sync(ascii) {
        function rightRotate(value, amount) {
            return (value >>> amount) | (value << (32 - amount));
        }

        const mathPow = Math.pow;
        const maxWord = mathPow(2, 32);
        const lengthProperty = 'length';
        let i, j;
        let result = '';

        const words = [];
        const asciiBitLength = ascii[lengthProperty] * 8;

        let hash = sha256Sync.h = sha256Sync.h || [];
        const k = sha256Sync.k = sha256Sync.k || [];
        let primeCounter = k[lengthProperty];

        const isComposite = {};
        for (let candidate = 2; primeCounter < 64; candidate++) {
            if (!isComposite[candidate]) {
                for (i = 0; i < 313; i += candidate) {
                    isComposite[i] = candidate;
                }
                hash[primeCounter] = (mathPow(candidate, 0.5) * maxWord) | 0;
                k[primeCounter++] = (mathPow(candidate, 1 / 3) * maxWord) | 0;
            }
        }

        hash = hash.slice(0);
        words[asciiBitLength >> 5] |= 0x80 << (24 - asciiBitLength % 32);
        words[(((asciiBitLength + 64) >> 9) << 4) + 15] = asciiBitLength;

        for (i = 0; i < ascii[lengthProperty]; i++) {
            words[i >> 2] |= ascii.charCodeAt(i) << ((3 - i % 4) * 8);
        }

        for (j = 0; j < words[lengthProperty]; j += 16) {
            const w = words.slice(j, j + 16);
            const oldHash = hash;
            hash = hash.slice(0, 8);

            for (i = 0; i < 64; i++) {
                const w15 = w[i - 15], w2 = w[i - 2];
                const s0 = rightRotate(w15, 7) ^ rightRotate(w15, 18) ^ (w15 >>> 3);
                const s1 = rightRotate(w2, 17) ^ rightRotate(w2, 19) ^ (w2 >>> 10);
                w[i] = (i < 16) ? w[i] : (
                    (w[i - 16] + s0 + w[i - 7] + s1) | 0
                );

                const s1_maj = rightRotate(hash[0], 2) ^ rightRotate(hash[0], 13) ^ rightRotate(hash[0], 22);
                const maj = (hash[0] & hash[1]) ^ (hash[0] & hash[2]) ^ (hash[1] & hash[2]);
                const t2 = (s1_maj + maj) | 0;

                const s1_ch = rightRotate(hash[4], 6) ^ rightRotate(hash[4], 11) ^ rightRotate(hash[4], 25);
                const ch = (hash[4] & hash[5]) ^ (~hash[4] & hash[6]);
                const t1 = (hash[7] + s1_ch + ch + k[i] + w[i]) | 0;

                hash = [(t1 + t2) | 0].concat(hash);
                hash[4] = (hash[4] + t1) | 0;
            }

            for (i = 0; i < 8; i++) {
                hash[i] = (hash[i] + oldHash[i]) | 0;
            }
        }

        for (i = 0; i < 8; i++) {
            for (j = 3; j >= 0; j--) {
                const b = (hash[i] >> (j * 8)) & 255;
                result += ((b < 16) ? '0' : '') + b.toString(16);
            }
        }
        return result;
    }

    /**
     * Geração de Checksum SHA-256 com Fallback FNV-1a
     */
    function generateChecksum(dataString) {
        if (typeof dataString !== 'string') {
            try {
                dataString = JSON.stringify(dataString);
            } catch (e) {
                dataString = String(dataString);
            }
        }
        try {
            return sha256Sync(dataString);
        } catch (e) {
            return hashFNV1a(dataString);
        }
    }

    /**
     * Sanitização e Validação Estrutural de JSON (Anti-Malicious Import)
     */
    function sanitizeCircuitImport(rawJsonString) {
        if (typeof rawJsonString !== 'string') {
            return { success: false, error: 'Formato inválido: esperado texto JSON.' };
        }

        if (rawJsonString.length > MAX_PAYLOAD_BYTES) {
            return { success: false, error: `Arquivo rejeitado: tamanho (${(rawJsonString.length / 1024 / 1024).toFixed(2)} MB) excede o limite seguro de 2 MB.` };
        }

        let parsed;
        try {
            parsed = JSON.parse(rawJsonString);
        } catch (e) {
            return { success: false, error: 'Falha ao processar arquivo: JSON malformado ou corrompido.' };
        }

        if (!parsed || (typeof parsed !== 'object')) {
            return { success: false, error: 'Estrutura inválida de circuito.' };
        }

        // Obtém whitelist de componentes autorizados
        const compEnum = window.LogicCraft?.core?.COMP || {};
        const allowedTypes = new Set(Object.values(compEnum));

        const rawComponents = Array.isArray(parsed) ? parsed : (Array.isArray(parsed.components) ? parsed.components : []);
        const rawAirWires = Array.isArray(parsed.airWires) ? parsed.airWires : [];
        const rawHoses = Array.isArray(parsed.hoses) ? parsed.hoses : [];

        const sanitizedComponents = [];
        for (const item of rawComponents) {
            if (!item || typeof item !== 'object') continue;

            // 1. Tipo estrito
            const typeStr = String(item.type || '').trim();
            if (allowedTypes.size > 0 && !allowedTypes.has(typeStr)) {
                // Descarta tipos desconhecidos ou maliciosos
                continue;
            }

            // 2. Coordenadas x, y, z
            const x = Number.isFinite(item.x) ? Math.max(BENCH_COORD_MIN, Math.min(BENCH_COORD_MAX, Math.round(item.x))) : 0;
            const y = Number.isFinite(item.y) ? Math.max(0, Math.min(10, Math.round(item.y))) : 0;
            const z = Number.isFinite(item.z) ? Math.max(BENCH_COORD_MIN, Math.min(BENCH_COORD_MAX, Math.round(item.z))) : 0;

            // 3. Rotação inteira 0..3
            const rotNum = Number.parseInt(item.rot, 10);
            const rot = (Number.isFinite(rotNum) && rotNum >= 0 && rotNum <= 3) ? rotNum : 0;

            // 4. Estados booleanos
            const manualValue = Boolean(item.manualValue);
            const stateVal = Boolean(item.state);

            // 5. Strings de tag e switchId (alfanumérico, sem caracteres perigosos)
            const cleanTag = cleanString(item.tag, MAX_TAG_LENGTH);
            const cleanSwitchId = cleanString(item.switchId, 4);

            sanitizedComponents.push({
                type: typeStr,
                x,
                y,
                z,
                rot,
                manualValue,
                state: stateVal,
                tag: cleanTag,
                switchId: cleanSwitchId,
                inputCount: (Number.isFinite(item.inputCount) && item.inputCount >= 1 && item.inputCount <= 4) ? item.inputCount : undefined,
                pressureBar: Number.isFinite(item.pressureBar) ? Math.max(0, Math.min(100, item.pressureBar)) : undefined,
                openingPercent: Number.isFinite(item.openingPercent) ? Math.max(0, Math.min(100, item.openingPercent)) : undefined,
                delayTime: Number.isFinite(item.delayTime) ? Math.max(0.1, Math.min(60, item.delayTime)) : undefined,
                posS0: Number.isFinite(item.posS0) ? Math.max(0, Math.min(1, item.posS0)) : undefined,
                posS1: Number.isFinite(item.posS1) ? Math.max(0, Math.min(1, item.posS1)) : undefined,
                isEmergencyLocked: Boolean(item.isEmergencyLocked)
            });
        }

        // Sanitização de Cabos Aéreos (permite jumpers e até 3 conexões por borne)
        const sanitizedAirWires = [];
        const pinWireCounts = new Map();
        for (const w of rawAirWires) {
            if (!w || !w.from || !w.to) continue;
            const sf = sanitizeEndpoint(w.from);
            const st = sanitizeEndpoint(w.to);
            if (sf && st) {
                // Previne conexões estritamente duplicadas entre os mesmos dois pinos
                const isDup = sanitizedAirWires.some(existing =>
                    ((existing.from.nodeX === sf.nodeX && existing.from.nodeZ === sf.nodeZ && existing.from.pinId === sf.pinId &&
                      existing.to.nodeX === st.nodeX && existing.to.nodeZ === st.nodeZ && existing.to.pinId === st.pinId) ||
                     (existing.from.nodeX === st.nodeX && existing.from.nodeZ === st.nodeZ && existing.from.pinId === st.pinId &&
                      existing.to.nodeX === sf.nodeX && existing.to.nodeZ === sf.nodeZ && existing.to.pinId === sf.pinId))
                );
                if (isDup) continue;

                // Permite até 3 conexões simultâneas no mesmo borne (jumpers/pontes de fechamento estrela/triângulo)
                const kFrom = `${sf.nodeX},${sf.nodeY},${sf.nodeZ}:${sf.pinId}`;
                const kTo = `${st.nodeX},${st.nodeY},${st.nodeZ}:${st.pinId}`;
                const cFrom = pinWireCounts.get(kFrom) || 0;
                const cTo = pinWireCounts.get(kTo) || 0;

                if (cFrom < 3 && cTo < 3) {
                    pinWireCounts.set(kFrom, cFrom + 1);
                    pinWireCounts.set(kTo, cTo + 1);
                    sanitizedAirWires.push({ from: sf, to: st });
                }
            }
        }

        // Sanitização de Tubulações
        const sanitizedHoses = [];
        for (const h of rawHoses) {
            if (!h || !h.from || !h.to) continue;
            const sf = sanitizeEndpoint(h.from, true);
            const st = sanitizeEndpoint(h.to, true);
            if (sf && st) {
                sanitizedHoses.push({
                    from: sf,
                    to: st,
                    hoseType: (h.hoseType === 'hydra' || h.hoseType === 'tank_return') ? h.hoseType : 'pneu'
                });
            }
        }

        return {
            success: true,
            data: {
                components: sanitizedComponents,
                airWires: sanitizedAirWires,
                hoses: sanitizedHoses
            }
        };
    }

    function cleanString(str, maxLen = 10) {
        if (!str || typeof str !== 'string') return '';
        return str
            .replace(/[<>&"'/\\;`]/g, '')
            .substring(0, maxLen)
            .trim();
    }

    function sanitizeEndpoint(pt, isFluid = false) {
        if (!pt || typeof pt !== 'object') return null;
        const nodeX = Number.isFinite(pt.nodeX) ? Math.max(BENCH_COORD_MIN, Math.min(BENCH_COORD_MAX, Math.round(pt.nodeX))) : 0;
        const nodeY = Number.isFinite(pt.nodeY) ? Math.max(0, Math.min(10, Math.round(pt.nodeY))) : 0;
        const nodeZ = Number.isFinite(pt.nodeZ) ? Math.max(BENCH_COORD_MIN, Math.min(BENCH_COORD_MAX, Math.round(pt.nodeZ))) : 0;

        const res = { nodeX, nodeY, nodeZ };
        if (isFluid) {
            res.portId = cleanString(pt.portId, 6);
        } else {
            res.pinId = cleanString(pt.pinId, 6);
        }
        return res;
    }

    /**
     * Armazenamento Seguro com Assinatura Checksum (LocalStorage Guard)
     */
    function secureSave(key, dataObj) {
        try {
            const jsonStr = JSON.stringify(dataObj);
            const hash = generateChecksum(jsonStr);
            const payload = {
                data: dataObj,
                hash: hash,
                timestamp: Date.now()
            };
            localStorage.setItem(key, JSON.stringify(payload));
            if (key === 'logiccraft_autosave_circuit') {
                localStorage.setItem('logiccraft_last_known_good', JSON.stringify(payload));
            }
            return true;
        } catch (e) {
            console.warn('[LogicCraftGuard] Falha ao salvar dados de forma segura:', e);
            return false;
        }
    }

    function secureLoad(key) {
        try {
            const raw = localStorage.getItem(key);
            if (!raw) return null;

            const envelope = JSON.parse(raw);
            if (!envelope || typeof envelope !== 'object' || envelope.data === undefined || !envelope.hash) {
                // Entrada legada ou corrompida: se possuir formato direto, retorna
                return envelope;
            }

            const dataStr = JSON.stringify(envelope.data);
            const expectedHash = generateChecksum(dataStr);

            if (envelope.hash !== expectedHash) {
                console.error(`[LogicCraftGuard] Alerta de Integridade: Checksum divergente na chave '${key}'. Restaurando estado padrão.`);
                localStorage.removeItem(key);
                if (window.LogicCraft?.ui?.showToast) {
                    window.LogicCraft.ui.showToast('warning', 'Integridade do circuito restaurada: alterações manuais no cache foram descartadas para evitar falhas.');
                }
                return null;
            }

            return envelope.data;
        } catch (e) {
            console.warn('[LogicCraftGuard] Falha ao carregar chave protegida:', e);
            try { localStorage.removeItem(key); } catch (_) {}
            return null;
        }
    }

    /**
     * Persistência de Progresso Pedagógico com Assinatura Hash (LocalStorage Guard)
     */
    function saveProgress(completedPhases) {
        if (!Array.isArray(completedPhases)) return false;
        return secureSave('logiccraft_progress', completedPhases);
    }

    function loadProgress() {
        const data = secureLoad('logiccraft_progress');
        if (Array.isArray(data)) return data;
        try {
            const raw = localStorage.getItem('logiccraft_progress');
            if (raw) {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) return parsed;
            }
        } catch (_) {}
        return [];
    }

    /**
     * Sanitização DOM e Construtores Seguros (XSS Prevention)
     */
    function cleanHTML(inputStr) {
        if (typeof inputStr !== 'string') return '';
        return inputStr
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    function createSafeElement(tag, classes = [], textContent = '') {
        const el = document.createElement(tag);
        if (Array.isArray(classes)) {
            classes.forEach(c => { if (c) el.classList.add(c); });
        } else if (typeof classes === 'string' && classes.trim()) {
            classes.trim().split(/\s+/).forEach(c => el.classList.add(c));
        }
        if (textContent !== undefined && textContent !== null && textContent !== '') {
            el.textContent = String(textContent);
        }
        return el;
    }

    /**
     * Purga e Desalocação de Materiais Órfãos na VRAM (Three.js VRAM Purge)
     */
    function purgeOrphanMaterials(scene, materialCache) {
        if (!scene || !materialCache) return 0;

        const activeMaterials = new Set();
        scene.traverse(obj => {
            if (obj.isMesh && obj.material) {
                if (Array.isArray(obj.material)) {
                    obj.material.forEach(m => activeMaterials.add(m));
                } else {
                    activeMaterials.add(obj.material);
                }
            }
        });

        let disposedCount = 0;
        if (materialCache instanceof Map) {
            for (const [key, mat] of materialCache.entries()) {
                if (!activeMaterials.has(mat)) {
                    if (mat.map && mat.map.dispose) mat.map.dispose();
                    if (mat.dispose) mat.dispose();
                    materialCache.delete(key);
                    disposedCount++;
                }
            }
        }
        return disposedCount;
    }

    /**
     * Modelo Térmico Bimetálico Normalizado (NR-10 / Eletrotécnica Industrial)
     * dT/dt = ((I / In)^2 - T) / tau
     * safeDt = Math.min(dt, 0.05); tau = 8.0s
     */
    function computeThermalStep(current, ratedCurrent, currentTemp = 0, dt = 0.016, tau = 8.0) {
        const safeDt = Math.min(dt, 0.05);
        const inNominal = ratedCurrent > 0 ? ratedCurrent : 5.0;
        const iRatio = current / inNominal;
        const targetEnergy = iRatio * iRatio; // Dissipação Joule proporcional a I^2

        const nextEnergy = currentTemp + (safeDt / tau) * (targetEnergy - currentTemp);
        return Math.max(0, nextEnergy);
    }

    /**
     * Monitor Global de Estabilidade e Tratamento de Falhas (Crash Interceptor)
     */
    let isCrashModalOpen = false;

    function showCrashModal(errorMessage, errorStack, errorFile, errorLine) {
        if (isCrashModalOpen) return;
        isCrashModalOpen = true;

        let modal = document.getElementById('logiccraft-crash-modal');
        if (!modal) {
            modal = document.createElement('div');
            modal.id = 'logiccraft-crash-modal';
            modal.style.cssText = `
                position: fixed;
                inset: 0;
                background: rgba(7, 21, 47, 0.94);
                backdrop-filter: blur(8px);
                z-index: 999999;
                display: flex;
                align-items: center;
                justify-content: center;
                font-family: 'Inter', system-ui, sans-serif;
                color: #FFFFFF;
                padding: 20px;
            `;

            const card = document.createElement('div');
            card.style.cssText = `
                background: #0B2048;
                border: 2px solid #DC2626;
                border-radius: 12px;
                max-width: 640px;
                width: 100%;
                box-shadow: 0 20px 40px rgba(0,0,0,0.7);
                padding: 24px;
                display: flex;
                flex-direction: column;
                gap: 16px;
            `;

            const titleRow = document.createElement('div');
            titleRow.style.cssText = 'display: flex; align-items: center; gap: 12px;';
            titleRow.innerHTML = `
                <div style="background:#7F1D1D; border-radius:50%; width:36px; height:36px; display:flex; align-items:center; justify-content:center; font-size:20px;">⚠️</div>
                <div>
                    <h2 style="margin:0; font-size:1.15rem; font-weight:800; color:#FFFFFF;">Falha Crítica do Simulador</h2>
                    <p style="margin:2px 0 0 0; font-size:0.8rem; color:#B7C5D8;">O motor de execução detectou uma exceção não tratada.</p>
                </div>
            `;

            const errorBox = document.createElement('pre');
            errorBox.id = 'crash-error-details';
            errorBox.style.cssText = `
                background: #07152F;
                border: 1px solid #30415D;
                border-radius: 6px;
                padding: 12px;
                font-family: 'JetBrains Mono', Consolas, monospace;
                font-size: 0.75rem;
                color: #F87171;
                max-height: 200px;
                overflow-y: auto;
                white-space: pre-wrap;
                word-break: break-all;
                margin: 0;
            `;

            const btnContainer = document.createElement('div');
            btnContainer.style.cssText = 'display: flex; gap: 12px; justify-content: flex-end; margin-top: 8px;';

            const btnRestore = document.createElement('button');
            btnRestore.textContent = 'Restaurar Último Ponto Estável';
            btnRestore.style.cssText = `
                background: #10B981;
                border: 1px solid #059669;
                color: #FFFFFF;
                font-weight: 700;
                padding: 12px 20px;
                border-radius: 6px;
                cursor: pointer;
                font-size: 0.875rem;
                transition: background 0.15s ease;
            `;
            btnRestore.onmouseover = () => { btnRestore.style.background = '#059669'; };
            btnRestore.onmouseout = () => { btnRestore.style.background = '#10B981'; };
            btnRestore.onclick = () => {
                try {
                    const lastGood = localStorage.getItem('logiccraft_last_known_good');
                    if (lastGood) {
                        localStorage.setItem('logiccraft_autosave_circuit', lastGood);
                    }
                } catch (_) {}
                modal.remove();
                isCrashModalOpen = false;
                window.location.reload();
            };

            const btnReset = document.createElement('button');
            btnReset.textContent = 'Reiniciar Bancada Limpa';
            btnReset.style.cssText = `
                background: #7F1D1D;
                border: 1px solid #DC2626;
                color: #FFFFFF;
                font-weight: 700;
                padding: 12px 20px;
                border-radius: 6px;
                cursor: pointer;
                font-size: 0.875rem;
                transition: background 0.15s ease;
            `;
            btnReset.onmouseover = () => { btnReset.style.background = '#991B1B'; };
            btnReset.onmouseout = () => { btnReset.style.background = '#7F1D1D'; };

            btnReset.onclick = () => {
                try {
                    localStorage.removeItem('logiccraft_autosave_circuit');
                } catch (_) {}
                if (window.LogicCraft?.simulation?.clearCircuit) {
                    try { window.LogicCraft.simulation.clearCircuit(); } catch (_) {}
                }
                modal.remove();
                isCrashModalOpen = false;
                window.location.reload();
            };

            btnContainer.appendChild(btnRestore);
            btnContainer.appendChild(btnReset);

            card.appendChild(titleRow);
            card.appendChild(errorBox);
            card.appendChild(btnContainer);
            modal.appendChild(card);
            document.body.appendChild(modal);
        }

        const pre = document.getElementById('crash-error-details');
        if (pre) {
            let locationInfo = '';
            if (errorFile || errorLine) {
                locationInfo = `Arquivo: ${errorFile || 'Desconhecido'} | Linha: ${errorLine || 'Desconhecida'}\n`;
            }
            pre.textContent = `${locationInfo}${errorMessage}\n\nPilha de Rastreamento:\n${errorStack || 'Sem rastreamento disponível'}`;
        }
    }

    // Registro de ouvintes de segurança no ambiente de execução
    if (typeof window !== 'undefined') {
        document.addEventListener('pointerlockerror', (e) => {
            // Interceptação silenciosa do pointerlock
            e.preventDefault();
            e.stopPropagation();
        }, true);
        
        window.addEventListener('error', (event) => {
            const msg = event.message || event.error?.message || '';
            const errName = event.error?.name || '';
            // Ignora explicitamente exceções relacionadas a Pointer Lock
            if (errName === 'SecurityError' || msg.includes('Pointer lock') || msg.includes('pointer lock') || msg.includes('Pointer lock cannot be acquired')) {
                event.preventDefault();
                return;
            }

            console.error('[LogicCraft Crash Interceptor]', event.error || event.message);
            if (event.filename && event.filename.includes('analytics')) return;
            showCrashModal(event.message || 'Erro de execução desconhecido', event.error?.stack, event.filename, event.lineno);
        });

        window.addEventListener('unhandledrejection', (event) => {
            const reason = event.reason;
            const msg = reason?.message || String(reason || '');
            const errName = reason?.name || '';
            // Ignora explicitamente exceções relacionadas a Pointer Lock
            if (errName === 'SecurityError' || msg.includes('Pointer lock') || msg.includes('pointer lock') || msg.includes('Pointer lock cannot be acquired')) {
                event.preventDefault();
                return;
            }

            console.error('[LogicCraft Rejection Interceptor]', event.reason);
            const stack = event.reason?.stack;
            showCrashModal(`Promessa Rejeitada: ${msg}`, stack);
        });
    }

    return {
        sanitizeCircuitImport,
        generateChecksum,
        secureSave,
        secureLoad,
        saveProgress,
        loadProgress,
        cleanHTML,
        createSafeElement,
        purgeOrphanMaterials,
        computeThermalStep,
        showCrashModal
    };
})();

